import { FunctionComponent, useCallback } from "react";
import { selectPlan } from "@store/plan/planReducer";
import { selectPlanItems } from "@store/plan/planReducer";
import { useAppSelector } from "@store/store";
import { useTranslation } from "react-i18next";
import { selectDirections } from "@store/planRouteReducer";
import { Image } from "@ui-kit/Image/Image";
import { Text } from "@ui-kit/Text/Text";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { PlanActionBar } from "../PlanActionBar/PlanActionBar";
import { StopSummary } from "../../tour/StopSummary";
import { TabButton } from "@ui-kit/TabButton/TabButton";
import { ExternalIcon } from "@icons/ExternalIcon";
import { PlanItemList } from "../../entities/PlanItemList/PlanItemList";
import { WithoutDaysHeader } from "../WithoutDaysHeader/WithoutDaysHeader";
import { DaySelector } from "../DaySelector/DaySelector";
import { EntityBackButton } from "@modules/components/buttons/EntityBackButton/EntityBackButton";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import { useNavigate } from "@hooks/useNavigate/useNavigate";
import { MapOverlayButton } from "@modules/components/buttons/MapOverButton/MapOverlayButton";

import "./PlanView.css";

const PlanView: FunctionComponent = () => {
  const { t } = useTranslation();
  const planItems = useAppSelector(selectPlanItems);
  const plan = useAppSelector(selectPlan);
  const directions = useAppSelector(selectDirections);
  const hasPlanDates = (plan?.dayDuration ?? 0) > 0;
  const { isMobile } = useWindowSize();
  const navigate = useNavigate();

  const handleNavigatePress = useCallback(() => {
    navigate("/plan?planView=navigate");
  }, [navigate]);

  const handleMapPress = useCallback(() => {
    navigate("/plan?planView=map");
  }, [navigate]);

  return (
    <>
      <div className="plan-list-item">
        {planItems?.length > 0 && (
          <div className="cover-photo">
            <Image src={planItems[0].plannable.coverPhotoUrl} />
          </div>
        )}
        <div className="plan-action-bar-container">
          {isMobile && (
            <div className="cover-actions-top-left">
              <EntityBackButton />
            </div>
          )}
          {planItems.length > 0 && <PlanActionBar />}
        </div>
      </div>
      <div className="plan-content">
        <Text variant="fontHeadingLarge">
          {plan?.name ?? t("screens.plan.defaultPlanTitle")}
        </Text>
      </div>
      <div className="navigation-container">
        <StopSummary
          planItems={planItems}
          routingEnabled={true}
          itemDirections={directions}
        />
        {directions && !isMobile && (
          <TabButton
            onPress={handleNavigatePress}
            label={t("map.navigate")}
            iconPrefix={
              <ExternalIcon
                svgName="play"
                color={colors.neutral700}
                size={20}
              />
            }
          />
        )}
      </div>
      <div className="plan-dates-container">
        {!hasPlanDates && <WithoutDaysHeader />}
        {hasPlanDates && <DaySelector />}
      </div>
      <div className="plan-item-list-container">
        <PlanItemList planItems={planItems} />
      </div>
      {isMobile && (
        <div className="mobile-map-button-container">
          <MapOverlayButton onPress={handleMapPress} />
        </div>
      )}
    </>
  );
};

export { PlanView };
